'use client';

import { useQuery, useSuspenseQuery } from '@tanstack/react-query';

import { AdminDashboardStats, getAdminDashboardStats } from '@website/services/adminDashboard.service';

export type { AdminDashboardStats };

export const adminDashboardStatsKey = ['admin', 'dashboard', 'stats'] as const;

/**
 * Hook to fetch admin dashboard statistics.
 * Returns counts for users, professionals, activities, bookings, and recent activity.
 */
export function useAdminDashboard() {
  return useQuery({
    queryKey: adminDashboardStatsKey,
    queryFn: async () => {
      try {
        return await getAdminDashboardStats();
      } catch (error) {
        throw new Error(
          `Failed to load admin dashboard stats: ${error instanceof Error ? error.message : 'Unknown error'}`
        );
      }
    },
    staleTime: 60 * 1000, // 1 minute - admin data can be slightly stale
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: 2
  });
}

/**
 * Suspense-enabled version of useAdminDashboard.
 * Use this with React Suspense boundaries for cleaner loading states.
 */
export function useAdminDashboardSuspense() {
  return useSuspenseQuery({
    queryKey: adminDashboardStatsKey,
    queryFn: async () => {
      return await getAdminDashboardStats();
    },
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000
  });
}
